// ReviewOrderPage — Review challenge order before activating
// Layout (matches base44 design):
//   - Top nav: < Back | "Review order" | Cancel
//   - Challenge header (tier badge + size)
//   - "Order details" section with inset list rows
//   - "Payment method" section
//   - "Total cost" + "Confirm activation" black button

import { useLocation, useNavigate } from 'react-router-dom';
import { TIERS, SIZE_OPTIONS_BY_TIER } from '../../data/platforms';
import { TierPill } from '../../components/Brand';

export function ReviewOrderPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const state = (loc.state as any) || { challenge: { tier: 'Beginner', size: '50', platform: 'Deepchart' }, amount: 79, shares: 1 };
  const { challenge, amount } = state;
  const tier = TIERS[challenge.tier as keyof typeof TIERS];
  const size = SIZE_OPTIONS_BY_TIER[challenge.tier as keyof typeof SIZE_OPTIONS_BY_TIER]?.find((s) => s.size === challenge.size);
  const fees = 0.00;

  return (
    <div className="min-h-screen bg-white pb-[calc(96px+env(safe-area-inset-bottom))]">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Review order</div>
        <button onClick={() => nav(-1)} className="px-3 text-[15px] text-tf-blue">Cancel</button>
      </div>

      <div className="flex items-center gap-3 px-4 pt-4">
        <TierPill tier={challenge.tier} />
        <div>
          <div className="text-[17px] font-bold text-tf-text-1">{challenge.tier} Challenge</div>
          <div className="text-[12px] text-tf-text-3">${challenge.size}K funded · {challenge.platform} · {tier.profitSplit}% split</div>
        </div>
      </div>

      <h2 className="mt-6 px-4 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Order details</h2>
      <div className="mt-2 mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Row label="Order type" value="Market buy" />
        <Row label="Tier" value={challenge.tier} />
        <Row label="Account size" value={`$${challenge.size}K funded`} />
        <Row label="Platform" value={challenge.platform} />
        <Row label="Estimated target" value={size ? `$${size.profitTarget.toLocaleString()}` : '—'} />
        <Row label="Max drawdown" value={size ? `$${size.maxDrawdown.toLocaleString()}` : '—'} />
        <Row label="Daily loss limit" value={size ? `$${size.dailyDrawdown.toLocaleString()}` : '—'} />
        <Row label="Profit split" value={`${tier.profitSplit}% to you`} />
        <Row label="Funded days minimum" value={`${tier.fundedDaysMin} days`} />
        <Row label="Activation fee" value="$0.00" />
        <Row label="Total to pay" value={`$${amount.toFixed(2)}`} bold last />
      </div>

      <h2 className="mt-6 px-4 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Payment method</h2>
      <div className="mt-2 mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <button className="flex w-full items-center justify-between border-b border-tf-border px-4 py-3 active:bg-tf-surface-1">
          <div className="flex items-center gap-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-tf-green-tint text-tf-profit">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2" /><path d="M2 10h20" /></svg>
            </div>
            <span className="text-[15px] text-tf-text-1">Card</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold text-tf-text-1">•••• 4242</span>
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
        </button>
        <div className="px-4 py-3 text-[13px] text-tf-text-3">Charged immediately. You can request a refund within 24 hours.</div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-tf-border bg-white/95 backdrop-blur-xl" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="mx-auto max-w-[640px] px-4 py-3 md:px-6">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[13px] text-tf-text-3">Total cost</span>
            <span className="text-[20px] font-bold tabular-nums text-tf-text-1" data-testid="review-total">${(amount + fees).toFixed(2)}</span>
          </div>
          <button
            onClick={() => nav('/portal/challenge/ch-' + Date.now(), { state: { activated: true } })}
            data-testid="confirm-buy"
            className="flex h-[52px] w-full items-center justify-center rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99]"
          >
            Confirm activation
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, last }: { label: string; value: React.ReactNode; bold?: boolean; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className={['text-[14px] text-tf-text-3', bold ? 'font-semibold text-tf-text-1' : ''].join(' ')}>{label}</span>
      <span className={['text-[14px] font-medium text-tf-text-1', bold ? 'text-[16px] font-bold' : ''].join(' ')}>{value}</span>
    </div>
  );
}
